import { useEffect } from 'react';

/**
 * Closes an open picker when the user clicks or taps outside it, or presses Escape.
 *
 * The three multi-select pickers in Field.jsx each opened a long scrolling panel that could only
 * be dismissed with a "Close" link underneath it — which, on a list of 400 attorneys, is below the
 * fold and reads as though the panel is stuck. Clicking away is what everyone tries first.
 *
 * `mousedown`, not `click`: a click fires after the mouse comes back up, so a user who presses
 * inside the panel and releases outside it (an accidental drag on a scrollable list, or a
 * text selection) would otherwise dismiss the panel mid-gesture. mousedown decides on the press,
 * which is where the user's intent actually is.
 *
 * The ref belongs on a node that contains BOTH the panel and whatever opens it. If it only wrapped
 * the panel, the button that opens it would count as "outside" and the panel would close on the
 * same press that opened it.
 *
 * @param {{current: HTMLElement|null}} ref     the container that counts as "inside"
 * @param {boolean} isOpen                      only listens while true, so a closed picker costs nothing
 * @param {() => void} onDismiss                called on an outside press or Escape
 */
export function useDismissOnOutside(ref, isOpen, onDismiss) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event) => {
      const node = ref.current;
      // A press on something already detached from the document (a row that just re-rendered)
      // is not an outside press — treating it as one would close the panel on its own updates.
      if (!node || !event.target || !document.contains(event.target)) return;
      if (node.contains(event.target)) return;
      onDismiss();
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onDismiss();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [ref, isOpen, onDismiss]);
}
